import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { usePublicInvitation, useAcceptInvitation } from '@/hooks/use-invitations';
import { ApiError } from '@/services/api-client';

export function InvitePage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const region = searchParams.get('region');

    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
    const { data: invitation, isLoading, isError, error } = usePublicInvitation(token, region);
    const acceptInvitation = useAcceptInvitation(region);

    const handleAccept = () => {
        if (!token) return;

        acceptInvitation.mutate(
            { token },
            {
                onSuccess: (result) => {
                    toast.success(`Welcome to ${result.tenant.name}!`);
                    navigate(`/tenants/${result.tenant.id}`);
                },
                onError: (err) => {
                    if (err instanceof ApiError && err.errorCode === 'email_mismatch') {
                        toast.error(
                            'Your account email does not match the email this invitation was sent to. Please sign in with the correct account.'
                        );
                    } else {
                        toast.error(err.message || 'Failed to accept invitation');
                    }
                },
            }
        );
    };

    const handleLogin = () => {
        loginWithRedirect({
            appState: { returnTo: `/invite?token=${token}&region=${region}` },
        });
    };

    // Missing query parameters
    if (!token || !region) {
        return (
            <Card className="mx-auto max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="text-destructive">Invalid Invitation Link</CardTitle>
                    <CardDescription>
                        This invitation link is incomplete. Please use the link from your invitation email.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                    <Button variant="outline" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (isLoading || authLoading) {
        return (
            <Card className="mx-auto max-w-md">
                <CardHeader className="text-center">
                    <Skeleton className="mx-auto h-6 w-48" />
                    <Skeleton className="mx-auto mt-2 h-4 w-64" />
                </CardHeader>
                <CardContent className="flex justify-center py-4">
                    <Spinner className="h-8 w-8" />
                </CardContent>
            </Card>
        );
    }

    if (isError || !invitation) {
        // Check for specific error codes
        const apiError = error instanceof ApiError ? error : null;
        const errorCode = apiError?.errorCode;

        let title = 'Invalid Invitation';
        let description = 'This invitation link is invalid, expired, or has already been used.';

        if (errorCode === 'invitation_expired') {
            title = 'Invitation Expired';
            description =
                'This invitation has expired. Please contact the organization administrator for a new invitation.';
        }

        return (
            <Card className="mx-auto max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="text-destructive">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                    <Button variant="outline" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const isExpired = new Date(invitation.expiresAt) < new Date();

    if (isExpired) {
        return (
            <Card className="mx-auto max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="text-destructive">Invitation Expired</CardTitle>
                    <CardDescription>
                        This invitation has expired. Please contact the organization administrator for a new
                        invitation.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                    <Button variant="outline" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="mx-auto max-w-md">
            <CardHeader className="text-center">
                <CardTitle>You're Invited!</CardTitle>
                <CardDescription>
                    {invitation.invitedByName
                        ? `${invitation.invitedByName} has invited you to join`
                        : 'You have been invited to join'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="rounded-lg border p-4 text-center">
                    <p className="text-lg font-semibold">{invitation.tenantName}</p>
                    <Badge variant="secondary" className="mt-2">
                        {invitation.role}
                    </Badge>
                </div>

                <div className="text-center text-sm text-muted-foreground">
                    <p>Invitation for: {invitation.email}</p>
                    <p>Expires: {new Date(invitation.expiresAt).toLocaleDateString()}</p>
                </div>

                {isAuthenticated ? (
                    <Button className="w-full" onClick={handleAccept} disabled={acceptInvitation.isPending}>
                        {acceptInvitation.isPending ? 'Accepting...' : 'Accept Invitation'}
                    </Button>
                ) : (
                    <div className="space-y-3">
                        <p className="text-center text-sm text-muted-foreground">
                            Sign in to accept this invitation
                        </p>
                        <Button className="w-full" onClick={handleLogin}>
                            Sign In to Accept
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
